import { c as _c } from "react/compiler-runtime";
import type { GraphOperationResult } from '@onefootprint/request-types/dashboard';
import { useQueryState } from 'nuqs';
import { useCallback } from 'react';
import { ACTIVE_NODE_QUERY_KEY } from '../playbook-builder.constants';
import useStore from './use-store';
const useSidebar = () => {
  const $ = _c(18);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      history: "replace"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [query, setQuery] = useQueryState(ACTIVE_NODE_QUERY_KEY, t0);
  const isAnyOpen = !!query;
  const {
    requestNodeFocus
  } = useStore();
  let t1;
  if ($[1] !== isAnyOpen || $[2] !== requestNodeFocus || $[3] !== setQuery) {
    t1 = (nodeId, options) => {
      setQuery(nodeId);
      if (options?.focusNode) {
        requestNodeFocus(nodeId);
      }
      if (options?.searchTerm) {
        const delay = isAnyOpen ? 400 : 200;
        setTimeout(() => highlightSearchTerm(options.searchTerm), delay);
      }
    };
    $[1] = isAnyOpen;
    $[2] = requestNodeFocus;
    $[3] = setQuery;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const open = t1;
  let t2;
  if ($[5] !== setQuery) {
    t2 = () => {
      setQuery(null);
    };
    $[5] = setQuery;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const close = t2;
  let t3;
  if ($[7] !== close || $[8] !== open) {
    t3 = result => {
      if (result?.kind === "add_node" && result.hasConfiguration) {
        open(result.rootNodeId);
      } else {
        close();
      }
    };
    $[7] = close;
    $[8] = open;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const onOperationComplete = t3;
  let t4;
  if ($[10] !== query) {
    t4 = nodeId_0 => query === nodeId_0;
    $[10] = query;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const isOpen = t4;
  let t5;
  if ($[12] !== close || $[13] !== isAnyOpen || $[14] !== isOpen || $[15] !== onOperationComplete || $[16] !== open) {
    t5 = {
      open,
      close,
      isAnyOpen,
      isOpen,
      onOperationComplete
    };
    $[12] = close;
    $[13] = isAnyOpen;
    $[14] = isOpen;
    $[15] = onOperationComplete;
    $[16] = open;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
};
const highlightSearchTerm = (term: string, attempt = 0) => {
  const HIGHLIGHT_DURATION = 2000;
  const RETRY_DELAYS = [100, 200, 400];
  const sidebar = document.querySelector('.node-panel-body');
  if (!sidebar || !term) return;
  const normalizedTerm = term.toLowerCase().trim();
  if (!normalizedTerm) return;

  // Build list of terms to match:
  // 1. Original term
  // 2. Term with underscores replaced by spaces
  // 3. Individual words (for partial matching like "first" in "First & Last name")
  const termWithSpaces = normalizedTerm.replace(/_/g, ' ');
  const individualWords = termWithSpaces.split(/\s+/).filter(w => w.length >= 3); // Only words with 3+ chars
  const termsToMatch = [...new Set([normalizedTerm, termWithSpaces, ...individualWords])];
  const marks: HTMLElement[] = [];
  const walker = document.createTreeWalker(sidebar, NodeFilter.SHOW_TEXT, {
    acceptNode: node => {
      const text = node.textContent?.toLowerCase() || '';
      return termsToMatch.some(t => text.includes(t)) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    }
  });
  const nodesToProcess: Text[] = [];
  let currentNode = walker.nextNode();
  while (currentNode) {
    nodesToProcess.push(currentNode as Text);
    currentNode = walker.nextNode();
  }

  // Build regex pattern that matches any of the terms (longer terms first to prefer full matches)
  const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const sortedTerms = [...termsToMatch].sort((a, b) => b.length - a.length);
  const pattern = sortedTerms.map(escapeRegex).join('|');
  const regex = new RegExp(`(${pattern})`, 'gi');
  for (const textNode of nodesToProcess) {
    const text = textNode.textContent || '';
    const parts = text.split(regex);
    if (parts.length > 1) {
      const fragment = document.createDocumentFragment();
      for (const part of parts) {
        if (termsToMatch.includes(part.toLowerCase())) {
          const mark = document.createElement('mark');
          mark.textContent = part;
          mark.style.backgroundColor = 'rgba(255, 213, 79, 0.8)';
          mark.style.transition = 'background-color 0.3s ease-out';
          marks.push(mark);
          fragment.appendChild(mark);
        } else {
          fragment.appendChild(document.createTextNode(part));
        }
      }
      textNode.parentNode?.replaceChild(fragment, textNode);
    }
  }

  // If no matches found and we have retries left, schedule another attempt
  if (marks.length === 0 && attempt < RETRY_DELAYS.length) {
    setTimeout(() => highlightSearchTerm(term, attempt + 1), RETRY_DELAYS[attempt]);
    return;
  }

  // No matches found after all retries - exit
  if (marks.length === 0) return;

  // Scroll to first match
  marks[0].scrollIntoView({
    behavior: 'smooth',
    block: 'center'
  });

  // Fade out and remove highlights after delay
  setTimeout(() => {
    for (const mark of marks) {
      mark.style.backgroundColor = 'transparent';
    }
    // Remove mark elements after fade
    setTimeout(() => {
      for (const mark of marks) {
        const text = document.createTextNode(mark.textContent || '');
        mark.parentNode?.replaceChild(text, mark);
      }
    }, 300);
  }, HIGHLIGHT_DURATION);
};
export default useSidebar;